import { addBrazeAlias, flushBrazeData, identifyBrazeUser, setBrazeEmailAndSubscriptions, startBrazeIfConsented, trackBrazeEvent } from './useBraze';
import { getBrazeFeatureConfig } from '@/services/featureService';
import type { SubscriptionState } from './index';
import type { Account } from '@/models/account/authModels';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { requestPushPermissionIfNeeded } from '@/services/pushNotificationService';

/**
 * Map user communication preferences to Braze subscription states
 */
export function mapPreferencesToBrazeSubscriptions(account: Account): {
    email?: SubscriptionState;
} {
    // User is opted in to email if ANY of these are true
    const emailOptedIn = account.receiveDonatosNewsAndEventEmails || account.ltdCommunicationEmail || account.lrmEmails || account.receivePersonalizedOffers || account.isMarketingEmail;

    // Only send OPTED_IN — never actively send UNSUBSCRIBED from a sync.
    // Unsubscribes must come from an explicit user action, not account flag evaluation.
    // Push subscription state is managed by Braze via device token registration.
    return {
        email: emailOptedIn ? 'OPTED_IN' : undefined,
    };
}

/**
 * Initialize Braze SDK on app launch and re-identify returning users.
 * syncUserToBraze only runs on explicit login, so returning users with persisted
 * sessions would never get changeUser called. We persist the customerId locally
 * to re-identify on every cold start without an extra API call.
 */
export async function initializeBraze(): Promise<void> {
    const hasConsent = true;
    await startBrazeIfConsented(hasConsent);

    if (Capacitor.isNativePlatform()) {
        const { value: customerId } = await Preferences.get({ key: 'braze_customer_id' });
        if (customerId) {
            // Check permissions BEFORE calling identifyBrazeUser so the push subscription
            // is passed atomically with changeUser(). Without this, changeUser() runs first
            // and Braze can sync from the server with UNSUBSCRIBED before the separate
            // setPushNotificationSubscriptionType call arrives.
            const permStatus = await PushNotifications.checkPermissions();
            const subs: { push?: SubscriptionState } | undefined = permStatus.receive === 'granted' ? { push: 'OPTED_IN' } : undefined;
            await identifyBrazeUser(customerId, undefined, subs);
        }
    }
}

/**
 * Sync user profile to Braze after login or profile update
 */
export async function syncUserToBraze(account: Account): Promise<void> {
    if (!account.customerId || !account.emailAddress) return;

    const subscriptions: { email?: SubscriptionState; push?: SubscriptionState } = mapPreferencesToBrazeSubscriptions(account);

    if (Capacitor.isNativePlatform()) {
        const permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'granted') {
            subscriptions.push = 'OPTED_IN';
        }
    }

    await identifyBrazeUser(account.customerId, account.emailAddress, subscriptions);
    await requestPushPermissionIfNeeded();
}

/**
 * Track user registration event
 */
export async function trackUserRegistered(customerId: string, email: string): Promise<void> {
    await trackBrazeEvent('user_registered', {
        customer_id: customerId,
        email,
    });
}

/**
 * Track communication preference updates
 */
export async function trackPreferenceUpdate(preferenceType: string, optedIn: boolean): Promise<void> {
    await trackBrazeEvent('preference_updated', {
        preference_type: preferenceType,
        opted_in: optedIn,
    });
}

/**
 * Attach a guest's email + marketing opt-in to the current anonymous Braze
 * profile. Does NOT call changeUser — same-device signup later merges this
 * anonymous profile into the identified one via changeUser(customerId).
 * Also tags the profile with an "email" user_alias so the server can call
 * Braze /users/identify after signup to merge cross-device guest profiles
 * into the identified customer.
 * Only call this when isMarketingEmail is true.
 */
export async function syncGuestEmailToBraze(email: string): Promise<void> {
    if (!email) return;
    const normalized = email.trim().toLowerCase();
    await addBrazeAlias(normalized, 'email');
    await setBrazeEmailAndSubscriptions(normalized, { email: 'OPTED_IN' });
}

/**
 * Link a guest’s anonymous Braze profile to their email before order submission, subject to guest-capture/opt-in rules
 * and a non-blocking timeout.
 */
const IDENTIFY_TIMEOUT_MS = 2000;

function withTimeout(work: Promise<void>, ms: number, label: string): Promise<void> {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const guard = new Promise<void>((resolve) => {
        timer = setTimeout(() => {
            console.log(`${label} timed out after ${ms}ms; continuing checkout`);
            resolve();
        }, ms);
    });

    const guarded = work.catch((error) => {
        console.log(`${label} failed`, error ? ` - ${error}` : '');
    });

    return Promise.race([guarded, guard]).finally(() => clearTimeout(timer));
}

export function identifyGuestForCheckout(email: string, isMarketingEmail: boolean): Promise<void> {
    if (!email) return Promise.resolve();
    return withTimeout(identifyGuest(email, isMarketingEmail), IDENTIFY_TIMEOUT_MS, 'Braze guest identification');
}

async function identifyGuest(email: string, isMarketingEmail: boolean): Promise<void> {
    if (isMarketingEmail) {
        await syncGuestEmailToBraze(email);
    } else {
        const { guestCapture } = await getBrazeFeatureConfig();
        if (!guestCapture) return;
        await addBrazeAlias(email.trim().toLowerCase(), 'email');
    }

    const delivered = await flushBrazeData();
    if (!delivered && !Capacitor.isNativePlatform()) {
        console.log('Braze flush not confirmed; order event may land on a separate alias profile');
    }
}
